import React, { useEffect, useState } from "react";
import { Compass, Quote, Loader2, Sparkles, ChevronDown, RefreshCw, Share2 } from "lucide-react";
import { toast } from "sonner";
import localApi from "@/lib/localApi";

const prettyToday = (iso) => {
  const d = iso ? new Date(iso + "T00:00:00") : new Date();
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
};

const DailyCompass = () => {
  const [dev, setDev] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showQ, setShowQ] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await localApi.get("/daily-compass/today");
      setDev(data);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const share = async () => {
    if (!dev) return;
    const prettyDate = (() => {
      try { return new Date(dev.date + "T00:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric" }); }
      catch { return ""; }
    })();
    const url = window.location.origin;
    const text =
      `🧭 The Daily Compass — ${prettyDate}\n\n` +
      `${dev.title}\n` +
      (dev.scripture ? `\n"${dev.scripture}"${dev.scripture_ref ? ` — ${dev.scripture_ref}` : ""}\n` : "") +
      (dev.affirmation ? `\n${dev.affirmation}\n` : "") +
      `\nReflect with us: ${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `The Daily Compass — ${dev.title}`, text, url });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Today's Compass copied — paste it to a friend 🧭");
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
        catch { toast.error("Could not share"); }
      }
    }
  };

  if (loading) {
    return (
      <div data-testid="daily-compass-loading" className="rounded-3xl border border-brass/30 bg-gradient-to-br from-primary/15 via-card to-card p-8 mb-8 flex items-center gap-3 text-muted-foreground">
        <Compass className="w-6 h-6 text-brass compass-spin" />
        <span>Charting today's Compass…</span>
      </div>
    );
  }

  if (error || !dev) {
    return (
      <div data-testid="daily-compass-error" className="rounded-3xl border border-border bg-card p-6 mb-8 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Compass className="w-5 h-5 text-brass" />
          <span>Today's Compass isn't ready yet.</span>
        </div>
        <button data-testid="daily-compass-retry" onClick={load}
          className="flex items-center gap-2 text-sm font-semibold text-brass hover:opacity-80">
          <RefreshCw className="w-4 h-4" /> Try again
        </button>
      </div>
    );
  }

  return (
    <div data-testid="daily-compass-card"
      className="relative overflow-hidden rounded-3xl border border-brass/30 bg-gradient-to-br from-primary/20 via-card to-nightsea/40 p-6 sm:p-8 mb-8 shadow-lg shadow-primary/10 animate-fade-up">
      <div className="absolute -right-10 -top-10 opacity-[0.07] pointer-events-none">
        <Compass className="w-56 h-56 text-brass compass-spin-slow" strokeWidth={1} />
      </div>

      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-brass">
              <Sparkles className="w-4 h-4" />
              <p className="text-xs uppercase tracking-[0.2em] font-semibold">AI Daily Compass</p>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{prettyToday(dev.date)}</p>
          </div>
          <button data-testid="daily-compass-share" onClick={share} title="Share today's Compass"
            className="flex items-center gap-2 text-sm font-semibold text-brass border border-brass/40 rounded-full px-4 py-2 hover:bg-brass/10 active:scale-95 transition-all shrink-0">
            <Share2 className="w-4 h-4" /> <span className="hidden sm:inline">Share</span>
          </button>
        </div>

        <h2 data-testid="daily-compass-title" className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mt-3 leading-tight">
          {dev.title}
        </h2>

        {dev.scripture && (
          <div className="mt-4 flex gap-3">
            <Quote className="w-5 h-5 text-brass shrink-0 mt-1" />
            <p className="font-heading italic text-lg text-foreground/90">
              "{dev.scripture}"
              {dev.scripture_ref && (
                <span className="block text-sm not-italic text-brass mt-1">— {dev.scripture_ref}</span>
              )}
            </p>
          </div>
        )}

        <p data-testid="daily-compass-reflection" className="mt-5 text-foreground/90 leading-loose whitespace-pre-wrap">
          {dev.reflection}
        </p>

        {dev.affirmation && (
          <div className="mt-5 rounded-2xl bg-nightsea/50 border border-brass/20 p-4">
            <p className="text-[11px] uppercase tracking-widest text-brass/80 font-semibold mb-1">Today's affirmation</p>
            <p className="font-heading text-lg italic text-foreground">{dev.affirmation}</p>
          </div>
        )}

        {Array.isArray(dev.questions) && dev.questions.length > 0 && (
          <div className="mt-5">
            <button data-testid="daily-compass-questions-toggle" onClick={() => setShowQ((v) => !v)}
              className="flex items-center gap-2 text-sm font-semibold text-primary hover:opacity-80">
              <ChevronDown className={`w-4 h-4 transition-transform ${showQ ? "rotate-180" : ""}`} />
              {showQ ? "Hide reflection questions" : `Reflect on ${dev.questions.length} questions`}
            </button>
            {showQ && (
              <ol className="mt-3 space-y-2" data-testid="daily-compass-questions">
                {dev.questions.map((q, i) => (
                  <li key={i} className="flex gap-3 bg-card/70 border border-border rounded-xl p-3">
                    <span className="w-6 h-6 rounded-full bg-brass/20 text-brass text-sm font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
                    <span className="text-foreground/90 leading-relaxed">{q}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyCompass;
